import { TFunction } from 'i18next';

import { CheckoutConfig, Session } from '../../types/checkout';
import { ExportToCsv } from 'export-to-csv';
import { formatDate, formatTime, toCommonCurrencyAmount, formatPhoneNumber } from '../../helpers/formatters';
import { getCustomerName, getCurrentEvent } from '../sessionHelpers';
import { getLocaleNumeralDecimal } from '../../helpers/intl';
import { downloadDataTablePdf } from '../../helpers/download/dataTablePdf';

export enum ActionTypes {
    GetCheckoutConfiguration = '[sessions] GET_CHECKOUT_CONFIGURATION',
    FetchCheckoutConfigurationResponse = '[sessions] FETCH_CHECKOUT_CONFIGURATION_RESPONSE',
    GetSessions = '[sessions] GET_SESSIONS',
    FetchSessionsResponse = '[sessions] FETCH_SESSIONS_RESPONSE',
    FetchNextResponse = '[sessions] FETCH_NEXT_RESPONSE',
    SwapSession = '[sessions] SWAP_SESSION',
}

export enum GetSessionReason {
    mount = 'mount',
    update = 'update',
    next = 'next',
    refresh = 'refresh',
}

export interface GetCheckoutConfiguration {
    type: ActionTypes.GetCheckoutConfiguration;
    payload: {
        accountIds: string[];
    };
}

export function getCheckoutConfiguration(accountIds: string[]): GetCheckoutConfiguration {
    return {
        type: ActionTypes.GetCheckoutConfiguration,
        payload: {
            accountIds: accountIds,
        },
    };
}

export interface FetchCheckoutConfigurationResponse {
    type: ActionTypes.FetchCheckoutConfigurationResponse;
    payload: {
        configurations: { [accountId: string]: CheckoutConfig };
    };
}

export interface GetSessions {
    type: ActionTypes.GetSessions;
    payload: {
        accountId: string;
        params: URLSearchParams;
        reason: GetSessionReason;
        requestId: number;
    };
}

export interface FetchSessionsResponse {
    type: ActionTypes.FetchSessionsResponse;
    payload: {
        sessions: Session[];
    };
    meta: {
        cause: GetSessions;
    };
}

export interface FetchNextResponse {
    type: ActionTypes.FetchNextResponse;
    payload: {
        sessions: Session[];
    };
    meta: {
        cause: FetchSessionsResponse;
    };
}

export function getSessions(accountId: string, params: URLSearchParams, origin: GetSessionReason): GetSessions {
    return {
        type: ActionTypes.GetSessions,
        payload: {
            accountId,
            params,
            reason: origin,
            requestId: performance.now(),
        },
    };
}

export interface SwapSession {
    type: ActionTypes.SwapSession;
    payload: {
        session: Session;
    };
}

const getCustomerProperty = (session: Session, property: 'phone_number' | 'email') => {
    if (session.order.billing_address && session.order.billing_address[property]) {
        return session.order.billing_address[property] || '';
    }
    if (session.order.shipping_address && session.order.shipping_address[property]) {
        return session.order.shipping_address[property] || '';
    }
    return '';
};

const mapSessionLine = (session: Session, t: TFunction, language: string) => ({
    [t('payments.fields.merchant_reference_non_abreviated')]: session.order.merchant_reference,
    [t('payments.fields.date')]: formatDate(session.created_at, language),
    [t('payments.fields.time')]: formatTime(session.created_at, language),
    [t('payments.fields.currency')]: session.order.currency,
    [t('payments.fields.amount')]: toCommonCurrencyAmount(session.order.amount, session.order.currency),
    [t('payments.fields.store')]: session.order.store?.id ?? '',
    [t('payments.fields.customer')]: getCustomerName(session),
    [t('payments.fields.phone_number')]: formatPhoneNumber(getCustomerProperty(session, 'phone_number')),
    [t('payments.fields.email')]: getCustomerProperty(session, 'email'),
    [t('payments.fields.status')]: t([
        `payments.virtual_terminal.poll_session.events.${getCurrentEvent(session).name}`,
        getCurrentEvent(session).name || '',
    ]),
    'Dintero ID': session.id,
});

export function createSessionsCsv(aid: string, sessions: Session[], t: TFunction, language: string): void {
    if (sessions.length === 0) {
        return;
    }
    const data = sessions.map((session) => mapSessionLine(session, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    const options = {
        fieldSeparator: ';',
        quoteStrings: '"',
        decimalSeparator: getLocaleNumeralDecimal(language),
        showLabels: true,
        showTitle: true,
        title: `Dintero sessions download ${aid}, ${nowFormatted}`,
        filename: `${aid}_${nowFormatted.replace(':', '-')}_Dintero-sessions`,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: true,
    };
    const csvExporter = new ExportToCsv(options);
    csvExporter.generateCsv(data);
}

export function createSessionsPdf(aid: string, sessions: Session[], t: TFunction, language: string): void {
    if (sessions.length === 0) {
        return;
    }
    const data = sessions.map((session) => mapSessionLine(session, t, language));
    const now = new Date().toISOString();
    const nowFormatted = `${formatDate(now, language)}T${formatTime(now, language)}`;
    downloadDataTablePdf(
        aid,
        `Dintero sessions download ${aid}, ${nowFormatted}`,
        nowFormatted,
        data,
        language,
        `${aid}_${nowFormatted.replace(':', '-')}_Dintero-sessions`,
        7
    );
}

export type Actions =
    | GetSessions
    | FetchSessionsResponse
    | FetchNextResponse
    | SwapSession
    | GetCheckoutConfiguration
    | FetchCheckoutConfigurationResponse;
